import { Building2, Handshake } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useSidebar } from '@/components/ui/sidebar'
import type { CompanyResponse } from '@super/shared-types'
import { formatUSPhoneForDisplay } from '@super/shared-types'

const TWENTY_ICONS_BASE_URL = 'https://twenty-icons.com'

interface CurrentCompanyDisplayProps {
  company: CompanyResponse
  isPartner?: boolean
}

export function CurrentCompanyDisplay({
  company,
  isPartner,
}: CurrentCompanyDisplayProps) {
  const { state } = useSidebar()
  const isCollapsed = state === 'collapsed'

  const domainUrl = company.domainName
  // Partners always show the Handshake icon
  const iconUrl =
    !isPartner && domainUrl ? `${TWENTY_ICONS_BASE_URL}/${domainUrl}` : null
  const FallbackIcon = isPartner ? Handshake : Building2

  return (
    <div
      className={cn(
        'flex w-full items-center gap-2 px-3 py-1.5 transition-all duration-200 bg-white border border-sidebar-border rounded-lg',
        isCollapsed && 'justify-center',
      )}
    >
      <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-xl border bg-muted text-sm font-medium overflow-hidden">
        {iconUrl ? (
          <img
            src={iconUrl}
            alt={`${company.name} icon`}
            className="object-contain w-full h-full"
            onError={e => {
              e.currentTarget.style.display = 'none'
              const fallbackIcon = e.currentTarget
                .nextElementSibling as HTMLElement
              if (fallbackIcon) {
                fallbackIcon.classList.remove('hidden')
              }
            }}
          />
        ) : null}
        <FallbackIcon className={cn('h-4 w-4', iconUrl && 'hidden')} />
      </div>
      {!isCollapsed && (
        <div className="flex flex-col flex-1 min-w-0">
          <span className="text-sm font-medium truncate">{company.name}</span>
          <span className="text-xs text-muted-foreground truncate">
            {isPartner
              ? 'Partner'
              : company.phone
                ? formatUSPhoneForDisplay(company.phone)
                : domainUrl || company.externalOrgId}
          </span>
        </div>
      )}
    </div>
  )
}
